import {useState, useEffect} from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {useNavigate, useLocation} from "react-router-dom";
import {isAxiosError} from "axios";
import Navbar from "../../Navbar/Navbar";
import SongForm from "../SongForm/SongForm";
import Button from "@mui/material/Button";
import {ISongInfoInput} from "../SongInfo/SongInfo";
import {ISongInputWrapper} from "../SongForm/SongForm";
import MessageBox from "../../MessageBox/MessageBox";
import "./SongPage.css";

const convertStringSecondsToTimeFormat = (secondsString: string): string => {
    const seconds = parseInt(secondsString, 10);
    if (isNaN(seconds) || seconds < 0) {
        return '0:00'; // Domyślny format w przypadku błędu
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface ISongInfoDB {
    id: number;
    title: string;
    lyrics: string;
    author: string;
    songNumber: string;
    length: string;
    lengthInt: number,
    plik?: string;
}

interface ISongInputDB {
    id: number;
    songNumber: number;
    lyrics: string;
    acords: string;
}

const createDefaultSongInfo = (songNumber: string): ISongInfoInput => ({
    id: 0,
    title: "",
    lyrics: "",
    author: "",
    length: "",
    songNumber: songNumber,
    lengthInt: 0
});

function findIndexBySongNumber(
    songList: ISongInfoInput[],
    targetSongNumber: string
) {
    let index = -1;
    for (let i = 0; i < songList.length; i++) {
        if (songList[i].songNumber === targetSongNumber) {
            index = i;
        }
    }
    return index;
}

const SongPage = () => {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    const bandId = sessionStorage.getItem("editedBandId");

    const INFO_URL = bandId ? `/admin/songs/${bandId}` : "/songs";
    const VERSE_URL = bandId ? `/admin/stanzas/${bandId}` : "/stanzas";
    const CHORUS_URL = bandId ? `/admin/refrains/${bandId}` : "/refrains";
    const MELODY_URL = bandId ? `/admin/melodies/${bandId}` : "/melodies";
    const SONG_URL = bandId ? `/admin/songs/${bandId}` : "/songs";

    const [open, setOpen] = useState(false);

    const [result, setResult] = useState(true);

    const [songNumber, setSongNumber] = useState(1);

    const [info, setInfo] = useState<ISongInfoInput[]>([]);
    const [input1, setInput1] = useState<ISongInputWrapper[]>([]);
    const [input2, setInput2] = useState<ISongInputWrapper[]>([]);
    const [input3, setInput3] = useState<ISongInputWrapper[]>([]);

    const [initialInfo, setInitialInfo] = useState<ISongInfoInput[]>([]);
    const [initialInput1, setInitialInput1] = useState<ISongInputWrapper[]>([]);
    const [initialInput2, setInitialInput2] = useState<ISongInputWrapper[]>([]);
    const [initialInput3, setInitialInput3] = useState<ISongInputWrapper[]>([]);

    const updateInfoAt = (index: number, newData: ISongInfoInput) => {
        if (index === -1) {
            // If no song exists yet, add it to the array
            setInfo(prevInfo => [...prevInfo, newData]);
        } else {
            // If song exists, update it
            setInfo(prevInfo => prevInfo.map((item, i) => i === index ? newData : item));
        }
    };

    function syncInputData() {
        if (songNumber === 1) {
            setInitialInput1(JSON.parse(JSON.stringify(input1)));
        } else if (songNumber === 2) {
            setInitialInput2(JSON.parse(JSON.stringify(input2)));
        } else if (songNumber === 3) {
            setInitialInput3(JSON.parse(JSON.stringify(input3)));
        }
        setInitialInfo(JSON.parse(JSON.stringify(info)));
    }

    function setCurrentNumber(number: number) {
        const inputArray = [input1, input2, input3];
        const initialInputArray = [initialInput1, initialInput2, initialInput3];
        const infoArray = info;
        const initialInfoArray = initialInfo;
        let currentSongIndex = findIndexBySongNumber(info, songNumber.toString());

        let hasChanged: boolean = false;

        setSongNumber(songNumber + number);

        if (
            inputArray[songNumber - 1].length !==
            initialInputArray[songNumber - 1].length
        ) {
            hasChanged = true;
        }

        if (!hasChanged) {
            for (let i = 0; i < initialInputArray[songNumber - 1].length; i++) {
                let currentInputElement = inputArray[songNumber - 1][i].data;
                let initialInputElement = initialInputArray[songNumber - 1][i].data;

                if (
                    currentInputElement.lyrics !== initialInputElement.lyrics ||
                    currentInputElement.acords !== initialInputElement.acords
                ) {
                    hasChanged = true;
                    break;
                }
            }

            let currentInfoElement = infoArray[currentSongIndex];
            let initialInfoElement = initialInfoArray[currentSongIndex];

            if ((initialInfoElement !== undefined && currentInfoElement !== undefined) && (
                currentInfoElement.title !== initialInfoElement.title ||
                currentInfoElement.lyrics !== initialInfoElement.lyrics ||
                currentInfoElement.author !== initialInfoElement.author ||
                currentInfoElement.length !== initialInfoElement.length
            )) {
                hasChanged = true;
            }
            if(currentInfoElement !== undefined && initialInfoElement === undefined) {
                hasChanged = true;
            }

        }

        const confirmDialog = (message: string) =>
            new Promise<void>((resolve, reject) => {
                if (window.confirm(message)) {
                    resolve();
                } else {
                    reject();
                }
            });

        if (hasChanged) {
            confirmDialog("Czy chcesz zapisać zmiany?")
                .then(async () => {
                    // post for new verses, choruses, melodies
                    let inputIndex = songNumber - 1;

                    let inputArrayLength = inputArray[inputIndex].length;
                    let initialInputArrayLength = initialInputArray[inputIndex].length;

                    if(inputArrayLength !== initialInputArrayLength) {
                        for(let i = inputArrayLength - initialInputArrayLength - 1; i <inputArrayLength; i++ ) {
                            let currentInputElement = inputArray[inputIndex][i];
                            if(currentInputElement.type === "melody") {
                                await axiosPrivate.post(
                                    MELODY_URL,
                                    JSON.stringify({
                                        id: currentInputElement.id,
                                        songNumber: songNumber,
                                        lyrics: currentInputElement.data.lyrics,
                                    }),
                                    {
                                        headers: {"Content-Type": "application/json"},
                                    }
                                );
                            } else if(currentInputElement.type === "verse") {
                                axiosPrivate.post(
                                    VERSE_URL,
                                    JSON.stringify({
                                        id: currentInputElement.id,
                                        songNumber: songNumber,
                                        acords: currentInputElement.data.acords,
                                        lyrics: currentInputElement.data.lyrics
                                    }),
                                    {
                                        headers: {"Content-Type": "application/json"},
                                    }
                                );
                            } else if(currentInputElement.type === "chorus") {
                                axiosPrivate.post(
                                    CHORUS_URL,
                                    JSON.stringify({
                                        id: currentInputElement.id,
                                        songNumber: songNumber,
                                        acords: currentInputElement.data.acords,
                                        lyrics: currentInputElement.data.lyrics
                                    }),
                                    {
                                        headers: {"Content-Type": "application/json"},
                                    }
                                );
                            }

                        }
                    }

                    for (let i = 0; i < initialInputArrayLength; i++) {
                        let currentInputElement = inputArray[inputIndex][i];
                        let initialInputElementData = initialInputArray[inputIndex][i].data;


                        if (currentInputElement.type === "melody") {
                            if (currentInputElement.data.lyrics !== initialInputElementData.lyrics) {
                                await axiosPrivate.post(
                                    MELODY_URL,
                                    JSON.stringify({
                                        id: currentInputElement.id,
                                        songNumber: songNumber,
                                        lyrics: currentInputElement.data.lyrics,
                                    }),
                                    {
                                        headers: {"Content-Type": "application/json"},
                                    }
                                );
                            }
                        } else {
                            if (currentInputElement.data.lyrics !== initialInputElementData.lyrics ||
                                currentInputElement.data.acords !== initialInputElementData.acords) {
                                if (currentInputElement.type === "verse") {
                                    await axiosPrivate.post(
                                        VERSE_URL,
                                        JSON.stringify({
                                            id: currentInputElement.id,
                                            songNumber: songNumber,
                                            acords: currentInputElement.data.acords,
                                            lyrics: currentInputElement.data.lyrics,
                                        }),
                                        {
                                            headers: { "Content-Type": "application/json" },
                                        }
                                    );
                                } else if (currentInputElement.type === "chorus") {
                                    await axiosPrivate.post(
                                        CHORUS_URL,
                                        JSON.stringify({
                                            id: currentInputElement.id,
                                            songNumber: songNumber,
                                            acords: currentInputElement.data.acords,
                                            lyrics: currentInputElement.data.lyrics,
                                        }),
                                        {
                                            headers: { "Content-Type": "application/json" },
                                        }
                                    );
                                }
                            }
                        }
            
                        const currentSong = info[currentSongIndex];
            
                        if (currentSong.lengthInt > 0) {
                            await axiosPrivate.post(
                                INFO_URL,
                                JSON.stringify({
                                    informations: currentSong.songNumber,
                                    title: currentSong.title,
                                    lyricist: currentSong.lyrics,
                                    musicAuthor: currentSong.author,
                                    songLength: currentSong.length,
                                    songNumber: currentSong.songNumber,
                                    songLengthInt: currentSong.lengthInt,
                                }),
                                {
                                    headers: { "Content-Type": "application/json" },
                                }
                            );
                        }
            
                        syncInputData();
                    }})
                    .catch((e) => {
                        alert("Anulowano zmiany");
                        if (songNumber === 1) {
                            setInput1(JSON.parse(JSON.stringify(initialInput1)));
                        } else if (songNumber === 2) {
                            setInput2(JSON.parse(JSON.stringify(initialInput2)));
                        } else if (songNumber === 3) {
                            setInput3(JSON.parse(JSON.stringify(initialInput3)));
                        }
                        const isMounted = true;
                        const controller = new AbortController();
                        fetchData(isMounted, controller).then(() => {});
                    });
            }
    }

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        fetchData(isMounted, controller).then(() => {
            if (isMounted) {
                info.forEach(element => {});
            }
        });

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, []);

    const fetchData = async (isMounted: boolean, controller: AbortController) => {
        const infoArray: ISongInfoDB[] = [];

        const getInfo = async () => {
            try {
                const response = await axiosPrivate.get(INFO_URL, {
                    signal: controller.signal,
                });

                isMounted &&
                response.data.forEach((s: ISongInfoDB) => {
                    infoArray.push(
                        {
                            id: s.id,
                            title: s.title,
                            lyrics: s.lyrics,
                            author: s.author,
                            length: s.length,
                            lengthInt: s.lengthInt,
                            songNumber: s.songNumber,
                        }
                    )
                });
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response && error.response.status === 403) {
                        navigate("/", {state: {from: location}, replace: true});
                    }
                }
            }
            return infoArray;
        };

        const getVerses = async () => {
            let verse1: ISongInputWrapper[] = [];
            let verse2: ISongInputWrapper[] = [];
            let verse3: ISongInputWrapper[] = [];
            try {
                const response = await axiosPrivate.get(VERSE_URL, {
                    signal: controller.signal,
                });

                isMounted &&
                response.data.forEach((v: ISongInputDB) => {
                    switch (v.songNumber) {
                        case 1:
                            verse1.push({
                                type: "verse",
                                data: {lyrics: v.lyrics, acords: v.acords},
                                id: v.id,
                            });
                            break;
                        case 2:
                            verse2.push({
                                type: "verse",
                                data: {lyrics: v.lyrics, acords: v.acords},
                                id: v.id,
                            });
                            break;
                        case 3:
                            verse3.push({
                                type: "verse",
                                data: {lyrics: v.lyrics, acords: v.acords},
                                id: v.id,
                            });
                            break;
                    }
                });
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response && error.response.status === 403) {
                        navigate("/", {state: {from: location}, replace: true});
                    }
                }
            }
            return [verse1, verse2, verse3];
        };

        const getChorus = async () => {
            let chorus1: ISongInputWrapper[] = [];
            let chorus2: ISongInputWrapper[] = [];
            let chorus3: ISongInputWrapper[] = [];

            try {
                const response = await axiosPrivate.get(CHORUS_URL, {
                    signal: controller.signal,
                });

                console.log(response);

                isMounted &&
                response.data.forEach((c: ISongInputDB) => {
                    switch (c.songNumber) {
                        case 1:
                            chorus1.push({
                                type: "chorus",
                                data: {lyrics: c.lyrics, acords: c.acords},
                                id: c.id,
                            });

                            break;
                        case 2:
                            chorus2.push({
                                type: "chorus",
                                data: {lyrics: c.lyrics, acords: c.acords},
                                id: c.id,
                            });
                            break;
                        case 3:
                            chorus3.push({
                                type: "chorus",
                                data: {lyrics: c.lyrics, acords: c.acords},
                                id: c.id,
                            });
                            break;
                    }
                });
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response && error.response.status === 403) {
                        navigate("/", {state: {from: location}, replace: true});
                    }
                }
            }

            return [chorus1, chorus2, chorus3];
        };

        const getMelody = async () => {
            let melody1: ISongInputWrapper[] = [];
            let melody2: ISongInputWrapper[] = [];
            let melody3: ISongInputWrapper[] = [];
            try {
                const response = await axiosPrivate.get(MELODY_URL, {
                    signal: controller.signal,
                });

                console.log("MELODY: ", response);

                isMounted &&
                response.data.forEach((m: ISongInputDB) => {
                    switch (m.songNumber) {
                        case 1:
                            melody1.push({
                                type: "melody",
                                data: {lyrics: m.lyrics, acords: ""},
                                id: m.id,
                            });

                            break;
                        case 2:
                            melody2.push({
                                type: "melody",
                                data: {lyrics: m.lyrics, acords: ""},
                                id: m.id,
                            });
                            break;
                        case 3:
                            melody3.push({
                                type: "melody",
                                data: {lyrics: m.lyrics, acords: ""},
                                id: m.id,
                            });
                            break;
                    }
                });
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response && error.response.status === 403) {
                        navigate("/", {state: {from: location}, replace: true});
                    }
                }
            }
            return [melody1, melody2, melody3];
        };

        const info = await getInfo();

        const verses = await getVerses();
        const melodies = await getMelody();
        const choruses = await getChorus();

        const data1 = [...verses[0], ...choruses[0], ...melodies[0]];
        const data2 = [...verses[1], ...choruses[1], ...melodies[1]];
        const data3 = [...verses[2], ...choruses[2], ...melodies[2]];

        setInfo(info);

        setInput1(data1.sort((a, b) => (a.id > b.id ? 1 : -1)));
        setInput2(data2.sort((a, b) => (a.id > b.id ? 1 : -1)));
        setInput3(data3.sort((a, b) => (a.id > b.id ? 1 : -1)));


        setInitialInfo(JSON.parse(JSON.stringify(info)));

        setInitialInput1(
            JSON.parse(JSON.stringify(data1.sort((a, b) => (a.id > b.id ? 1 : -1))))
        );
        setInitialInput2(
            JSON.parse(JSON.stringify(data2.sort((a, b) => (a.id > b.id ? 1 : -1))))
        );
        setInitialInput3(
            JSON.parse(JSON.stringify(data3.sort((a, b) => (a.id > b.id ? 1 : -1))))
        );
    };

    const resetData = async () => {
        try {
            await axiosPrivate.delete(SONG_URL, {
                headers: {"Content-Type": "application/json"},
                data: {
                    songNumber: songNumber,
                },
            });

            setInfo((prev) => {
                const updatedInfo = prev.filter(song => song.songNumber !== songNumber.toString());
                console.log("Updated info after deletion:", updatedInfo);
                return updatedInfo;
            });

            console.log("SongNumber: ", songNumber);

            switch (songNumber) {
                case 1:
                    setInput1([]);
                    break;
                case 2:
                    setInput2([]);
                    break;
                case 3:
                    setInput3([]);
                    break;
            }

            setResult(true);
        } catch (error) {
            if (isAxiosError(error)) {
                if (error.response && error.response.status === 403) {
                    navigate("/", {state: {from: location}, replace: true});
                }
                setResult(false);
            }
        }

        setOpen(true);
    };

    return (
        <div className="song-container">
            <Navbar/>
            <div className="song-content">
                <div className="song-select">
                    <Button
                        variant="contained"
                        size="large"
                        disabled={songNumber <= 1}
                        onClick={() => setCurrentNumber(-1)}
                    >
                        <span>Poprzednia piosenka</span>
                    </Button>
                    <h2>Piosenka {songNumber}</h2>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={songNumber >= 3}
                        onClick={() => setCurrentNumber(1)}
                    >
                        <span>Następna piosenka</span>
                    </Button>
                </div>
                {songNumber === 1 && (
                    <SongForm
                        songNumber={1}
                        info={info[findIndexBySongNumber(info, "1")] || createDefaultSongInfo("1")}
                        setInfo={(newData: ISongInfoInput) => updateInfoAt(findIndexBySongNumber(info, "1"), newData)}
                        setInput={setInput1}
                        input={input1}
                        syncInputData={syncInputData}
                        reset={resetData}
                        fetchData={fetchData}
                    />
                )}
                {songNumber === 2 && (
                    <SongForm
                        songNumber={2}
                        info={info[findIndexBySongNumber(info, "2")] || createDefaultSongInfo("2")}
                        setInfo={(newData: ISongInfoInput) => updateInfoAt(findIndexBySongNumber(info, "2"), newData)}
                        setInput={setInput2}
                        input={input2}
                        syncInputData={syncInputData}
                        reset={resetData}
                        fetchData={fetchData}
                    />
                )}
                {songNumber === 3 && (
                    <SongForm
                        songNumber={3}
                        info={info[findIndexBySongNumber(info, "3")] || createDefaultSongInfo("3")}
                        setInfo={(newData: ISongInfoInput) => updateInfoAt(findIndexBySongNumber(info, "3"), newData)}
                        setInput={setInput3}
                        input={input3}
                        syncInputData={syncInputData}
                        reset={resetData}
                        fetchData={fetchData}
                    />
                )}
            </div>
            <MessageBox open={open} setOpen={setOpen} error={!result}/>
        </div>
    );
};

export default SongPage;